import { useState } from 'react'
import { useIntl } from 'react-intl'
import { useDialogState } from 'ariakit/dialog'
import {
  IconArrowLineUp,
  Menu,
  MenuButton,
  MenuItem,
  Tooltip,
  useMenuState,
  useToast,
} from '@vtex/admin-ui'
import { ImportModal } from '@/components/products-listing/components/ImportModal/ImportModal'
import { type ImportResponseProps } from '@/components/products-listing/hooks/useImportRequest'
import { useUserPermissionsContext } from '@/contexts/userPermissions'

import * as style from './ImportMenu.styles'

export type ImportMenuProps = {
  postImportRequest: (type: ImportType, file: File) => void
  hasFile: File | null
  setHasFile: (file: File | null) => void
  importType: ImportType
  setImportType: (type: ImportType) => void
  importResponse: Record<string, ImportResponseProps>
}

export type ImportType =
  | 'Product'
  | 'Image'
  | 'ProductSpecification'
  | 'SkuSpecification'

export const ImportMenu = ({
  postImportRequest,
  hasFile,
  setHasFile,
  importType,
  setImportType,
  importResponse,
}: ImportMenuProps) => {
  const state = useMenuState()
  const { formatMessage } = useIntl()
  const importModal = useDialogState()
  const showToast = useToast()
  const { hasImportPermission } = useUserPermissionsContext()

  const [visibleTooltip, setVisibleTooltip] = useState(false)

  const handleSelectType = (type: ImportType) => {
    const existingImport = Object.values(importResponse).find(
      (response) => response.Type === type && response.Status !== 'Completed'
    )

    if (existingImport) {
      showToast({
        variant: 'warning',
        duration: 2000,
        message: formatMessage({
          id: 'admin/admin.catalog.import-menu.import-disabled-message',
          defaultMessage:
            'Import disabled. You can only import one file at a time per option.',
        }),
      })
      return
    }

    setImportType(type)
    importModal.setOpen(true)
  }

  return (
    <>
      <Tooltip
        placement="top"
        visible={visibleTooltip && !hasImportPermission}
        text={formatMessage({
          id: 'admin/admin.catalog.import-export-menu.permission-error',
          defaultMessage:
            "Your user role doesn't have permission to perform this action. Contact your account administrator.",
        })}
      >
        <div
          onMouseEnter={() => {
            setVisibleTooltip(true)
          }}
          onMouseLeave={() => {
            setVisibleTooltip(false)
          }}
        >
          <MenuButton
            state={state}
            variant="neutralTertiary"
            disabled={!hasImportPermission}
            label={
              <>
                <IconArrowLineUp aria-hidden />
                {formatMessage({
                  id: 'admin/admin.catalog.import-menu.import-button-label',
                  defaultMessage: 'Import',
                })}
              </>
            }
            className={style.menuButton}
          />
        </div>
      </Tooltip>
      <Menu state={state} aria-label="actions" className={style.menuActions}>
        <MenuItem
          label={formatMessage({
            id: 'admin/admin.catalog.import-export-menu.products-and-skus-label',
            defaultMessage: 'Products and SKUs',
          })}
          onClick={() => {
            handleSelectType('Product')
          }}
        />
        <MenuItem
          label={formatMessage({
            id: 'admin/admin.catalog.import-export-menu.images-label',
            defaultMessage: 'Images',
          })}
          onClick={() => {
            handleSelectType('Image')
          }}
        />
        <MenuItem
          label={formatMessage({
            id: 'admin/admin.catalog.import-export-menu.product-specifications-label',
            defaultMessage: 'Product Specifications',
          })}
          onClick={() => {
            handleSelectType('ProductSpecification')
          }}
        />
        <MenuItem
          label={formatMessage({
            id: 'admin/admin.catalog.import-export-menu.sku-specifications-label',
            defaultMessage: 'SKU Specifications',
          })}
          onClick={() => {
            handleSelectType('SkuSpecification')
          }}
        />
      </Menu>
      <ImportModal
        state={importModal}
        postImportRequest={postImportRequest}
        hasFile={hasFile}
        setHasFile={setHasFile}
        importType={importType}
        setImportType={setImportType}
      />
    </>
  )
}
